import parsePhoneNumber from 'libphonenumber-js';
import { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { z } from 'zod';

import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { PhoneInput } from '@sage/ui-components';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledDataList from 'src/components/StyledDataList';
import StyledLinearProgressBar from 'src/components/StyledLinearProgressBar';
import { useOnboardingWorkflow } from 'src/contexts/OnboardingWorkflowContext';
import {
    selectHasConsentedToEmailMarketing,
    selectOnboardingUser,
    setHasConfirmedUserInfo,
    setHasConsentedToEmailMarketing,
    setOnboardingUserDetails,
} from 'src/store/slices/onboardingSlice';
import { useAppDispatch } from 'src/store/store';

const confirmInfoSearchSchema = z.object({
    isEditInfo: z.boolean().default(false),
});

export const Route = createFileRoute('/(app)/onboarding/confirm-info')({
    component: ConfirmInfoComponent,
    validateSearch: zodValidator(confirmInfoSearchSchema),
});

function ConfirmInfoComponent() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isEditInfo } = Route.useSearch();

    const onboardingUser = useSelector(selectOnboardingUser);
    const hasConsentedToEmailMarketing = useSelector(selectHasConsentedToEmailMarketing);

    const { progress, nextStep } = useOnboardingWorkflow();

    // Form for edit mode
    const { handleSubmit, control, formState, watch, reset } = useForm<{
        firstName: string;
        lastName: string;
        phoneNumber: string;
        email: string;
        hasConsented: boolean;
    }>({
        mode: 'onChange',
        defaultValues: {
            firstName: onboardingUser?.firstName || '',
            lastName: onboardingUser?.lastName || '',
            phoneNumber: onboardingUser?.phoneNumber || '',
            email: onboardingUser?.email || '',
            hasConsented: hasConsentedToEmailMarketing ?? false,
        },
    });

    const phoneNumber = useWatch({ control, name: 'phoneNumber' });
    const hasConsented = watch('hasConsented');

    useEffect(() => {
        // Reset the form when the onboarding user changes
        if (onboardingUser) {
            reset({
                firstName: onboardingUser.firstName || '',
                lastName: onboardingUser.lastName || '',
                phoneNumber: onboardingUser.phoneNumber || '',
                email: onboardingUser.email || '',
                hasConsented: hasConsentedToEmailMarketing ?? false,
            });
        }
    }, [onboardingUser, hasConsentedToEmailMarketing, reset]);

    const handleNext = () => {
        dispatch(setHasConfirmedUserInfo(true));
        dispatch(setHasConsentedToEmailMarketing(hasConsented));
        nextStep();
    };

    const handleSaveEdit = (data: {
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        hasConsented: boolean;
    }) => {
        dispatch(
            setOnboardingUserDetails({
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: data.phoneNumber,
                email: data.email,
            })
        );
        dispatch(setHasConfirmedUserInfo(true));
        dispatch(setHasConsentedToEmailMarketing(data.hasConsented));

        // Go directly to next step after editing
        nextStep();
    };

    // Shared header text and button logic
    const headerText = isEditInfo ? 'Update your information' : 'Verify your information';

    const primaryButtonAction = isEditInfo ? () => handleSubmit(handleSaveEdit)() : handleNext;

    const isButtonDisabled = isEditInfo ? !formState.isValid : false;

    const infoData = useMemo(
        () => [
            {
                term: 'Full Name',
                definition: `${onboardingUser?.firstName} ${onboardingUser?.lastName}`,
            },
            {
                term: 'Phone Number',
                definition:
                    parsePhoneNumber(onboardingUser?.phoneNumber ?? '')?.formatNational() ||
                    onboardingUser?.phoneNumber,
            },
            { term: 'Email', definition: onboardingUser?.email },
        ],
        [onboardingUser]
    );

    const bodyContent = isEditInfo ? (
        <Stack sx={{ gap: 4 }}>
            <Controller
                name="firstName"
                control={control}
                rules={{ required: 'First name is required' }}
                render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                    <TextField
                        inputRef={ref}
                        fullWidth
                        label="First Name"
                        variant="outlined"
                        value={value}
                        onChange={onChange}
                        error={!!error}
                        helperText={error?.message}
                    />
                )}
            />

            <Controller
                name="lastName"
                control={control}
                rules={{ required: 'Last name is required' }}
                render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                    <TextField
                        inputRef={ref}
                        fullWidth
                        label="Last Name"
                        variant="outlined"
                        value={value}
                        onChange={onChange}
                        error={!!error}
                        helperText={error?.message}
                    />
                )}
            />

            <Controller
                name="email"
                control={control}
                rules={{
                    required: 'Email is required',
                    pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                    },
                }}
                render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                    <TextField
                        inputRef={ref}
                        fullWidth
                        label="Email"
                        variant="outlined"
                        value={value}
                        onChange={onChange}
                        error={!!error}
                        helperText={error?.message}
                    />
                )}
            />

            <PhoneInput disabled value={phoneNumber} />
        </Stack>
    ) : (
        <StyledDataList data={infoData} />
    );

    return (
        <Box>
            <PageHeader hideBackButton={!isEditInfo} hideActionButton>
                <StyledLinearProgressBar value={progress.percentage} />
            </PageHeader>
            <Stack sx={{ gap: 3.5 }}>
                <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                    {headerText}
                </Typography>

                {bodyContent}
            </Stack>
            <PageFooter>
                {!isEditInfo && (
                    <Button
                        variant="text"
                        fullWidth
                        color="primary"
                        onClick={() =>
                            navigate({
                                to: '/onboarding/confirm-info',
                                search: { isEditInfo: true },
                            })
                        }
                        sx={{ ':visited': { color: 'primary.main' } }}
                    >
                        Edit your information
                    </Button>
                )}
                <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    onClick={primaryButtonAction}
                    disabled={isButtonDisabled}
                >
                    Confirm
                </Button>
            </PageFooter>
        </Box>
    );
}
